import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Shared chrome for /terms and /privacy.
 *
 * Redesigned 2026-04-30 to match the new landing aesthetic:
 * Inter body, Geist display, teal #14808A accent, hairline borders.
 * The "branded island" comment in LegalPage.css explains why this
 * surface ignores the user's app theme.
 */

type Props = {
  children: React.ReactNode;
  /** Hero title rendered in the page header band. */
  title: string;
  /** Eyebrow ("Legal", "Privacy", etc) sitting above the title. */
  eyebrow?: string;
  /** Last-updated string ("Last updated: April 1, 2026"). */
  updated?: string;
};

const LegalLayout: React.FC<Props> = ({ children, title, eyebrow = 'Legal', updated }) => {
  const navigate = useNavigate();
  const [showToTop, setShowToTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowToTop(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="legal-page">
      <header className="legal-header">
        <div className="legal-header-inner">
          <button className="legal-back" onClick={() => navigate(-1)} aria-label="Go back">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 2 5 7l5 5" />
            </svg>
            Back
          </button>
          <Link to="/" className="legal-wordmark">
            PRTCL<span className="legal-wordmark__dot">.</span>
          </Link>
        </div>
      </header>

      <section className="legal-hero">
        <div className="legal-hero-inner">
          <span className="legal-eyebrow">{eyebrow}</span>
          <h1 className="legal-title">{title}</h1>
          {updated && <p className="legal-updated">{updated}</p>}
        </div>
      </section>

      {children}

      <footer className="legal-page-footer">
        <div className="legal-page-footer-inner">
          <div className="legal-page-footer__wordmark">
            PRTCL<span className="legal-wordmark__dot">.</span>
          </div>
          <div className="legal-page-footer__links">
            <Link to="/privacy">Privacy</Link>
            <Link to="/consumer-health-data">Consumer Health Data</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/">Home</Link>
          </div>
          <span className="legal-page-footer__copy">© {new Date().getFullYear()} PRTCL</span>
        </div>
      </footer>

      <button
        type="button"
        className={`legal-to-top ${showToTop ? 'is-visible' : ''}`}
        onClick={scrollTop}
        aria-label="Back to top"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
    </div>
  );
};

export default LegalLayout;
